import { Lock } from "lucide-react";
import { T, withAlpha } from "../tokens";

// A blurred taste of the deep analysis behind a lock — shown on a not-yet-scored
// match so the user SEES the value (skill bars, skill-by-skill) before deciding to
// spend a free preview / a credit / buy. The sample is generic + blurred (clearly
// a teaser, never real per-match data). The CTA is computed by the caller so the
// same teaser drives the free, use-a-credit, and buy states.

export interface TeaserCta {
  label:     string;
  sub:       string;
  disabled?: boolean;
  onClick:   () => void;
}

const SAMPLE_BARS = [
  { label: "Skills fit",    pct: 84 },
  { label: "Title fit",     pct: 72 },
  { label: "Seniority fit", pct: 66 },
  { label: "Domain fit",    pct: 79 },
];
const SAMPLE_SKILLS = [
  "React · core strength",
  "Node.js · strong match",
  "AWS · listed as preferred",
];

export function LockedAnalysisTeaser({ cta, error }: { cta: TeaserCta; error?: string | null }) {
  return (
    <section style={{ position: "relative", marginTop: 22, borderRadius: 12, overflow: "hidden",
      border: `1px solid ${T.tealBdr}`, background: T.s1 }}>

      {/* Blurred sample of what the deep analysis looks like */}
      <div aria-hidden style={{ filter: "blur(5px)", opacity: 0.5, pointerEvents: "none",
        userSelect: "none", padding: "18px 18px 10px" }}>
        {SAMPLE_BARS.map((b) => (
          <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <span style={{ fontFamily: T.body, fontSize: 12, color: T.t1, width: 90, flexShrink: 0 }}>{b.label}</span>
            <div style={{ flex: 1, height: 6, background: T.s2, borderRadius: 99, overflow: "hidden" }}>
              <div style={{ width: `${b.pct}%`, height: "100%", background: `linear-gradient(90deg, ${withAlpha(T.teal, 0.53)}, ${T.teal})` }}/>
            </div>
          </div>
        ))}
        <div style={{ height: 1, background: T.b0, margin: "12px 0" }}/>
        {SAMPLE_SKILLS.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.tealSoft, flexShrink: 0 }}/>
            <span style={{ fontFamily: T.body, fontSize: 13, color: T.t0 }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Lock overlay + the real CTA */}
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 9, padding: 20, textAlign: "center",
        background: `linear-gradient(180deg, ${withAlpha(T.s1, 0.85)}, ${withAlpha(T.s1, 0.96)})` }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.tealBg, border: `1px solid ${T.tealBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", color: T.tealSoft }}>
          <Lock size={16}/>
        </div>
        <h3 style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, color: T.t0, margin: 0 }}>
          See your full fit analysis
        </h3>
        <p style={{ fontFamily: T.body, fontSize: 12, color: T.t2, margin: 0, maxWidth: 300, lineHeight: 1.5 }}>
          Skill-by-skill match, seniority &amp; domain fit, and red flags — scored for this exact role.
        </p>
        <button type="button" onClick={cta.onClick} disabled={cta.disabled} style={{
          marginTop: 4, padding: "10px 18px", borderRadius: 8, border: "none",
          background: cta.disabled ? T.s3 : T.teal, color: cta.disabled ? T.t2 : "#04120E",
          fontFamily: T.body, fontSize: 13, fontWeight: 700, cursor: cta.disabled ? "default" : "pointer" }}>
          {cta.label}
        </button>
        <span style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, letterSpacing: "0.04em" }}>{cta.sub}</span>
        {error && <span style={{ fontFamily: T.body, fontSize: 11, color: T.err, marginTop: 2 }}>{error}</span>}
      </div>
    </section>
  );
}
